import {FormInstance} from 'antd/es/form';
import {NamePath} from 'antd/es/form/interface';
import {isArray, isEqual} from 'lodash-es';
import moment, {Moment} from 'moment';
import {useEffect, useRef, useState} from 'react';

import {IFieldMeta, IValidationRules} from 'entities/metadata';
import {
    EFormFieldWatcherActions,
    EFormFieldWatcherConditions,
    EFormFieldWatcherConnective,
    ESameDateUnits
} from 'shared/constants/watchers';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {IFormFieldWatcher, IFormFieldWatcherCondition, TDisabledDateConfig} from 'shared/types/watchers';
import {evaluateBoolean} from 'shared/utils/boolean-evaluator';
import {isValueInEnum} from 'shared/utils/is-value-in-enum';
import {createConnectiveJoiner} from 'shared/utils/watchers';

import {EFormMode} from '../../form-types';
import {EComparisonType, FORM_WATCHER_EVENT} from './use-form-watcher-constants';
import {getCustomWatchers} from './use-form-watcher-custom-watchers';
import {checkRangeConstraints, checkSameDateUnitConstraints} from './use-form-watcher-utils';

interface IFormValuesChangeEventDetail {
    values: { [i: string]: any };
    targetForm?: any;
}

interface IDispatchFormWatcherEventArgs extends IFormValuesChangeEventDetail {
}

export const dispatchFormWatcherEvent = (args: IDispatchFormWatcherEventArgs) => {
    window.dispatchEvent(new CustomEvent(FORM_WATCHER_EVENT, {detail: args}));
};

type TCreateActionHandlersArgs = {
    detail: IFormValuesChangeEventDetail;
    watcher: IFormFieldWatcher;
}

export interface ICreateConditionCallbacksArgs {
    condition: EFormFieldWatcherConditions;
    ownValue?: any;
    targetValue: any;
    withKey?: IFormFieldWatcherCondition['withKey'];
    withValue?: IFormFieldWatcherCondition['withValue'];
    withValueAsNumber?: IFormFieldWatcher['withOwnValueAsNumber'];
    withOwnValueAsNumber?: IFormFieldWatcher['withOwnValueAsNumber'];
    target: IFormFieldWatcher['conditions'][0]['target'];
    dateRange?: IFormFieldWatcherCondition['dateRange'];

    withTargetToPickAttributeFrom?: string;
    withTargetAttributeKey?: string;
    withMapping?: {
        [index: string]: string;
    };
}

interface IUseFormWatcherArgs {
    fieldMeta: IFieldMeta;
    form?: FormInstance<any>;
    formMode?: EFormMode;
    entityName?: string;
}

export type TValueTypes = string | number | Date;

// лимит погружения в вызове dispatch
const MAX_DISPATCH_DEPTH_ALLOWED = 5;

export const useFormWatcher = ({
    fieldMeta, form, formMode, entityName
}: IUseFormWatcherArgs) => {
    const [isHidden, setIsHidden] = useState(false);
    const [isRequired, setIsRequired] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [isAllowedEmpty, setIsAllowedEmpty] = useState(false);
    const [validationRules, setValidationRules] = useState<IValidationRules[]>([]);
    const [shouldDispatch, setShouldDispatch] = useState<boolean>(false);
    const [dependentFieldValue, setDependentFieldValue] = useState();
    const [referenceUrlByWatcher, setReferenceUrlByWatcher] = useState<string | undefined>(undefined);
    const [disabledDateConfig, setDisabledDateConfig] = useState<TDisabledDateConfig>();
    const lastFetchedAttributeRef = useRef<string | null | undefined>(undefined);
    const attributeRef = useRef<any>(undefined);
    const isSyncingRef = useRef(false);
    const dispatchDepthRef = useRef(0);
    const {hasUserRoleFunction} = useUserRoleFunctions({sections: [entityName ?? '']});

    const formValues = form?.getFieldsValue();
    const lastValues = useRef<{ [i: string]: any }>(formValues);

    useEffect(() => {
        if (!formValues) return;
        if (isSyncingRef.current) return; // предотвращаем повторный вход, пока идёт обработка
        if (!isEqual(lastValues.current, formValues)) {
            isSyncingRef.current = true;
            setShouldDispatch(true);
            lastValues.current = formValues;
            // Сбрасываем флаг после того, как React завершит обновление
            setTimeout(() => {
                isSyncingRef.current = false;
            }, 0);
        }
    }, [formValues]);

    const depthRef = useRef(0);

    const setLastValues = (newValues: { [i: string]: any }) => {
        lastValues.current = newValues;
        setShouldDispatch(true);
    };

    const shouldPerformWatcherTargetsUpdate = (prevValues: any, currentValues: any) => fieldMeta
        ?.watchers?.map((d: any) => d.target)
        .some((key: string) => prevValues[key] !== currentValues[key]);

    const compareValues = (
        value: TValueTypes,
        comparableValue : TValueTypes,
        comparisonType: string,
    ) => {
        switch (comparisonType) {
        case EComparisonType.EQUALS:
            return value === comparableValue;
        case EComparisonType.GREATER:
            return value > comparableValue;
        case EComparisonType.GREATER_OR_EQUAL:
            return value >= comparableValue;
        case EComparisonType.LESS:
            return value < comparableValue;
        case EComparisonType.LESS_OR_EQUAL:
            return value <= comparableValue;
        default:
            return false;
        }
    };

    const checkIfWatcherIsTriggered = ({
        condition,
        ownValue, // значение поля, на котором стоит watcher
        withOwnValueAsNumber,
        targetValue, // значение поля, которое таргетит watcher
        withValue, // значение для сравнения со значением поля, на котором стоит watcher
        withKey,
        withValueAsNumber,
        target,
        withTargetToPickAttributeFrom,
        withTargetAttributeKey,
    }: ICreateConditionCallbacksArgs) => {
        const ownValueNormalized = withOwnValueAsNumber ? Number(ownValue) : ownValue || !!ownValue;
        const targetValueNormalized = withValueAsNumber ? Number(targetValue) : targetValue || !!targetValue;
        const withValueNormalized = withValueAsNumber ? Number(withValue) : withValue;
        const callbacks = {
            [EFormFieldWatcherConditions.always]: true,
            [EFormFieldWatcherConditions.equalsByKeyOrToValue]: (() => {
                if (withKey && targetValue?.[withKey]) {
                    const normalized = withValueAsNumber ? Number(targetValue[withKey]) : targetValue[withKey];
                    return normalized === withValueNormalized;
                }
                return targetValueNormalized === withValueNormalized;
            })(),
            [EFormFieldWatcherConditions.formCreating]: formMode === EFormMode.CREATE,
            [EFormFieldWatcherConditions.formEditing]: formMode === EFormMode.EDIT,
            [EFormFieldWatcherConditions.set]: !!targetValueNormalized,
            [EFormFieldWatcherConditions.isFieldSet]: (() => {
                if (!withKey) return false;
                return !!targetValueNormalized[withKey];
            })(),
            [EFormFieldWatcherConditions.unset]: !targetValueNormalized || targetValueNormalized.length === 0,
            [EFormFieldWatcherConditions.equals]: ownValueNormalized === targetValueNormalized,
            [EFormFieldWatcherConditions.fieldEqualsToValue]: (() => {
                if (!withKey) return false;
                const normalized = withValueAsNumber ? Number(targetValue?.[withKey]) : targetValue?.[withKey];
                return normalized === withValueNormalized;
            })(),
            [EFormFieldWatcherConditions.fieldEqualsToValues]: (() => {
                if (!withKey) return false;
                return String(withValue)
                    ?.split(',')
                    .map(value => value.trim())
                    .includes(targetValue?.[withKey]);
            })(),
            [EFormFieldWatcherConditions.compareFieldValues]: (() => {
                if (!withValue) return false;
                if (fieldMeta.type === 'NUMBER' || fieldMeta.inputType === 'INPUT_NUMBER') {
                    return compareValues(Number(ownValue), Number(targetValue), withValue);
                }
                return compareValues(ownValue, targetValue, withValue);
            })(),
            [EFormFieldWatcherConditions.equalsToValue]: targetValueNormalized === withValueNormalized,
            [EFormFieldWatcherConditions.notEqualsToValue]: withValueNormalized !== targetValueNormalized,
            [EFormFieldWatcherConditions.refererenceValueHasBeenChanged]: (() => {
                if (!target || !withTargetToPickAttributeFrom || !withTargetAttributeKey) return false;
                const newValue = targetValueNormalized?.[withTargetAttributeKey];
                if (newValue && attributeRef.current === undefined) {
                    attributeRef.current = newValue;
                    return true;
                }
                if (!isEqual(newValue, attributeRef.current)) {
                    attributeRef.current = newValue;
                    return true;
                }
                return false;
            })(),
            [EFormFieldWatcherConditions.hasRole]: (() => {
                if (!withValue) return false;
                return hasUserRoleFunction(withValue);
            })(),
            [EFormFieldWatcherConditions.unique]: false,
        };
        return callbacks[condition] ?? false;
    };

    const createActionHandlers = ({
        watcher, detail,
    }: TCreateActionHandlersArgs) => {
        const {
            conditions,
            perform: actions,
            withOwnValueToSet,
            withOwnValueAsNumber,
            withValidationRules,
            withErrorToSet,

            withTargetToPickAttributeFrom,
            withTargetAttributeKey,
            withMapping,

            conditionsExpression,
        } = watcher;

        const {values} = detail;

        const ownValue = values?.[fieldMeta.key];

        let isWatcherTriggered: boolean | null = null;

        if (conditionsExpression) {
            let expression = conditionsExpression;

            conditions.forEach(({
                target,
                when: condition,
                withValue,
                withKey,
                name,
                withValueAsNumber,
                dateRange,
            }) => {
                if (name) {
                    expression = expression.split(name).join((() => {
                        const targetValue = target ? values?.[target] : undefined;
                        const isTriggered = checkIfWatcherIsTriggered({
                            condition,
                            targetValue,
                            ownValue,
                            withKey,
                            withValue,
                            withValueAsNumber,
                            withOwnValueAsNumber,
                            target,
                            withTargetToPickAttributeFrom,
                            withTargetAttributeKey,
                            withMapping,
                            dateRange,
                        });
                        return `${isTriggered}`;
                    })());
                }
            });

            isWatcherTriggered = evaluateBoolean(expression);
        } else {
            conditions?.forEach(({
                target,
                when: condition,
                withValue,
                withKey,
                withConnective,
                withValueAsNumber,
                dateRange,
            }) => {
                const targetValue = target ? values?.[target] : undefined;
                const isTriggered = checkIfWatcherIsTriggered({
                    condition,
                    targetValue,
                    ownValue,
                    withKey,
                    withValue,
                    withValueAsNumber,
                    withOwnValueAsNumber,
                    target,
                    withTargetToPickAttributeFrom,
                    withTargetAttributeKey,
                    withMapping,
                    dateRange,
                });

                const joinCondition = createConnectiveJoiner(withConnective);
                if (isWatcherTriggered === null) {
                    isWatcherTriggered = withConnective === EFormFieldWatcherConnective.NOT
                        ? !isTriggered : isTriggered;
                } else isWatcherTriggered = joinCondition(isWatcherTriggered, isTriggered);
            });
        }

        const handlers = {
            [EFormFieldWatcherActions.clear]: () => {
                if (isWatcherTriggered) form?.setFieldsValue({[fieldMeta.key]: undefined});
            },
            [EFormFieldWatcherActions.disable]: () => {
                if (isWatcherTriggered) setIsDisabled(true);
                else setIsDisabled(false);
            },
            [EFormFieldWatcherActions.enableField]: () => {
                if (isWatcherTriggered) setIsDisabled(false);
                else setIsDisabled(true);
            },
            [EFormFieldWatcherActions.require]: () => {
                if (isWatcherTriggered) setIsRequired(true);
                else {
                    setIsRequired(false);
                    form?.validateFields([fieldMeta.key]);
                }
            },
            [EFormFieldWatcherActions.hide]: () => {
                if (isWatcherTriggered) setIsHidden(true);
                else setIsHidden(false);
            },
            [EFormFieldWatcherActions.setOwnValue]: () => {
                if (isWatcherTriggered) {
                    if (withOwnValueToSet === '$null') form?.setFieldsValue({[fieldMeta.key]: null});
                    else if (withOwnValueAsNumber) {
                        form?.setFieldsValue({[fieldMeta.key]: Number(withOwnValueToSet)});
                    } else form?.setFieldsValue({[fieldMeta.key]: withOwnValueToSet});
                }
            },
            [EFormFieldWatcherActions.validate]: () => {
                if (isWatcherTriggered) form?.validateFields([fieldMeta.key]);
            },
            [EFormFieldWatcherActions.allowEmpty]: () => {
                if (isWatcherTriggered) setIsAllowedEmpty(true);
                else setIsAllowedEmpty(false);
            },
            [EFormFieldWatcherActions.addValidationRules]: () => {
                if (isWatcherTriggered) {
                    setValidationRules(p => (
                        [...p, ...(withValidationRules || [])]));
                }
            },
            [EFormFieldWatcherActions.addFieldError]: () => {
                if (isWatcherTriggered) {
                    setValidationRules(p => (
                        [...p, {mask: 'errorWatcher', errorMessage: withErrorToSet}]));
                }
            },
            [EFormFieldWatcherActions.fetchDataToCustomSelect]: () => {
                if (isWatcherTriggered && withTargetToPickAttributeFrom && withTargetAttributeKey) {
                    const mainTargetValue = form?.getFieldValue(withTargetToPickAttributeFrom as NamePath);
                    const fetchAttribute = mainTargetValue?.[withTargetAttributeKey];
                    const fetchPath = (() => {
                        const valuePath = fieldMeta?.path?.value;
                        if (!withMapping || !mainTargetValue || !valuePath) return null;
                        return withMapping[mainTargetValue[valuePath]];
                    })();
                    if (fetchPath && fetchAttribute && fetchAttribute !== lastFetchedAttributeRef.current) {
                        lastFetchedAttributeRef.current = fetchAttribute;
                        setReferenceUrlByWatcher(`${fetchPath}/${fetchAttribute}`);
                    }
                }
            },
            [EFormFieldWatcherActions.disableDate]: () => {
                if (isWatcherTriggered) {
                    const dateRangeCondition = conditions.find(condition => condition.dateRange);
                    if (!dateRangeCondition?.target) return;

                    const {dateRange, target} = dateRangeCondition;
                    const {unit, amount, type} = dateRange || {};
                    const targetValue = form?.getFieldValue(target);

                    const disabledDate = (currentValue: Moment) => {
                        const targetDate = moment(targetValue);
                        if (!dateRange || !targetValue || !targetDate.isValid() || (!targetValue && !ownValue)) return false;

                        const isSameDateUnit = isValueInEnum(unit, ESameDateUnits);

                        if (type === 'end') {
                            return isSameDateUnit ?
                                checkSameDateUnitConstraints(currentValue, targetDate, false, unit) :
                                checkRangeConstraints(currentValue, targetDate, false, unit, amount);
                        }

                        if (type === 'start') {
                            return isSameDateUnit ?
                                checkSameDateUnitConstraints(currentValue, targetDate,true, unit) :
                                checkRangeConstraints(currentValue, targetDate, true, unit, amount);
                        }

                        return false;
                    };
                    setDisabledDateConfig({
                        disabledDate,
                        dateRange: dateRange || {},
                    });
                }
            },
        };

        setLastValues(values);
        if (withTargetToPickAttributeFrom && withTargetAttributeKey) {
            attributeRef.current = form?.getFieldValue(
                withTargetToPickAttributeFrom as NamePath,
            )?.[withTargetAttributeKey];
        }
        if (isArray(actions)) return actions.map(action => handlers?.[action]);
        return [handlers?.[actions]];
    };

    useEffect(() => {
        const handler = ({detail}: any) => {
            if (!form) return;

            // временное решение, задача TAXMONSUPP-3680, необходимо разобраться, почему закольцовывается выполнение
            // dispatch, что приводит к зависанию страницы
            if (depthRef.current > MAX_DISPATCH_DEPTH_ALLOWED) {
                return;
            }
            depthRef.current += 1;

            try {
                const {targetForm} = detail as IFormValuesChangeEventDetail;
                if (targetForm && form !== targetForm) return;

                if (fieldMeta?.dependentInputKey) {
                    const formDependentFieldValue = targetForm?.getFieldValue(fieldMeta?.dependentInputKey);
                    if (formDependentFieldValue) {
                        setDependentFieldValue(formDependentFieldValue);
                    }
                }

                const fieldWatchers = getCustomWatchers(fieldMeta, entityName);

                setValidationRules([]);
                fieldWatchers?.forEach(watcher => {
                    createActionHandlers({
                        watcher,
                        detail,
                    }).forEach(actionHandler => {
                        actionHandler?.();
                    });
                });
            } finally {
                depthRef.current -= 1;
            }
        };
        window.addEventListener(FORM_WATCHER_EVENT, handler);
        return () => {
            window.removeEventListener(FORM_WATCHER_EVENT, handler);
        };
    }, []);

    useEffect(() => {
        if (shouldDispatch) {
            if (dispatchDepthRef.current > MAX_DISPATCH_DEPTH_ALLOWED) {
                setShouldDispatch(false);
                return;
            }
            dispatchDepthRef.current += 1;
            dispatchFormWatcherEvent({ values: formValues, targetForm: form });
            setShouldDispatch(false);
            // Сбрасываем глубину после завершения цикла событий
            setTimeout(() => {
                dispatchDepthRef.current -= 1;
            }, 0);
        }
    }, [shouldDispatch]);

    /**
     * Инициализация watchers при открытии формы в режиме создания.
     * Используется планирование (setTimeout) для отложенного выполнения после завершения текущего рендера.
     * Это гарантирует, что все поля формы будут проинициализированы перед запуском watchers,
     * предотвращая пропуск событий из-за внутренних оптимизаций Ant Design.
     */
    useEffect(() => {
        const hasWatchers = fieldMeta?.watchers?.length;

        if (!hasWatchers || formMode !== EFormMode.CREATE) return undefined;

        const timeoutId = setTimeout(() => {
            if (form) {
                dispatchFormWatcherEvent({values: form.getFieldsValue(), targetForm: form});
            }
        }, 0);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [form, formMode]);

    return {
        shouldPerformWatcherTargetsUpdate,
        isHidden,
        isRequired,
        isDisabled,
        isAllowedEmpty,
        validationRules,
        referenceUrlByWatcher,
        dependentFieldValue,
        disabledDateConfig,
    };
};
